import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import {
  Home,
  User,
  Target,
  BookOpen,
  Plus,
  Calendar,
  Megaphone,
  Code2,
  MoreHorizontal,
  Sparkles,
  Trophy,
  Camera,
  CalendarDays,
  Users,
  MessageCircle,
  Bot,
  BarChart3,
  Search,
  Bell,
  PlayCircle,
  Bookmark,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

export default function Navigation() {
  const location = useLocation();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const [messagesRes, notificationsRes] = await Promise.all([
          axios.get(`${API}/messages/unread-count`),
          axios.get(`${API}/notifications/unread-count`),
        ]);
        setUnreadMessages(messagesRes.data.unread_count);
        setUnreadNotifications(notificationsRes.data.count);
      } catch (error) {
        // Silent fail - user might not be logged in
      }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const mainNavItems = [
    { path: "/feed", icon: Home, label: "Feed" },
    { path: "/explore", icon: Search, label: "Explore" },
    { path: "/reels", icon: PlayCircle, label: "Reels" },
    { path: "/create", icon: Plus, label: "Post", isCreate: true },
    { path: "/notifications", icon: Bell, label: "Alerts", badge: unreadNotifications },
    { path: "/profile", icon: User, label: "Profile" },
  ];

  const moreItems = [
    { path: "/messages", icon: MessageCircle, label: "Messages", badge: unreadMessages },
    { path: "/friends", icon: Users, label: "Friends" },
    { path: "/joy", icon: Bot, label: "Joy AI" },
    { path: "/wellness", icon: Sparkles, label: "Life Training" },
    { path: "/goals", icon: Target, label: "Goals" },
    { path: "/planner", icon: CalendarDays, label: "Planner" },
    { path: "/saved", icon: Bookmark, label: "Saved Posts" },
    { path: "/events", icon: Calendar, label: "Events" },
    { path: "/achievements", icon: Trophy, label: "Achievements" },
    { path: "/camera", icon: Camera, label: "AI Camera" },
    { path: "/gratitude", icon: BookOpen, label: "Gratitude Journal" },
    { path: "/analytics", icon: BarChart3, label: "Analytics" },
    { path: "/ads", icon: Megaphone, label: "Promote Post" },
    { path: "/developer", icon: Code2, label: "Developer API" },
  ];

  const isMoreActive = moreItems.some((item) => location.pathname === item.path);

  return (
    <nav className="nav-dock" data-testid="main-navigation">
      <div className="flex items-center gap-1">
        {mainNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;

          if (item.isCreate) {
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid="nav-create-btn"
                className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 active:scale-95 -mt-4"
              >
                <Icon className="w-6 h-6" />
              </NavLink>
            );
          }

          return (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}-btn`}
              className={`relative flex flex-col items-center gap-1 px-3 py-2 rounded-2xl transition-all ${
                isActive
                  ? "text-primary bg-primary/10"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-xs font-medium">{item.label}</span>
              {item.badge > 0 && (
                <span className="absolute -top-1 right-0 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                  {item.badge > 9 ? "9+" : item.badge}
                </span>
              )}
            </NavLink>
          );
        })}

        {/* More dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="nav-more-btn"
              className={`flex flex-col items-center gap-1 px-3 py-2 rounded-2xl transition-all ${
                isMoreActive
                  ? "text-primary bg-primary/10"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              <MoreHorizontal className="w-5 h-5" />
              <span className="text-xs font-medium">More</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 mb-2">
            {moreItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <DropdownMenuItem
                  key={item.path}
                  asChild
                  className={isActive ? "bg-primary/10" : ""}
                >
                  <NavLink
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}-btn`}
                    className="flex items-center gap-2 w-full"
                  >
                    <Icon className={`w-4 h-4 ${isActive ? "text-primary" : ""}`} />
                    <span>{item.label}</span>
                  </NavLink>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  );
}
